import { useQuery } from '@tanstack/react-query';

import type { GetShipSellerFormsToEkhoResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get ship seller forms to Ekho data from a purchased vehicle
 */
export const getShipSellerFormsToEkho = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetShipSellerFormsToEkhoResponse> => {
	const response = await apiClient.get<GetShipSellerFormsToEkhoResponse>(
		ordersEndpoints.shipSellerFormsToEkho(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting ship seller forms to Ekho data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetShipSellerFormsToEkho = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.shipSellerFormsToEkho.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get ship seller forms to Ekho data without an order ID.');
			}
			return getShipSellerFormsToEkho(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
